import { useState, useEffect, useCallback } from 'react';
import { chatApi } from '../services/chatapi';
import { AI_PROVIDER_CHANGED_EVENT } from '../utils/aiProvider';

/**
 * Custom hook for managing AI models
 * @returns {Object} Hook properties and methods
 */
const useAiModels = () => {
  const [models, setModels] = useState([]);
  const [currentModel, setCurrentModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);

  // Load models from API. #492: `force` bypasses the shared model cache — used on a
  // provider switch, where the catalogue changes; the mount fetch uses the cache so the
  // chat panel + per-item tools don't each hit /models when they mount together.
  const fetchModels = useCallback(async ({ force = false } = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await chatApi.getAvailableModels({ force });

      if (response && response.models) {
        setModels(response.models);
        
        // Store metadata about model source
        if (response.metadata) {
          setMetadata(response.metadata);
        }
        
        // Set current model — T1.24: only ever select a model that actually
        // exists in the fetched list, so we never default to one the active
        // provider can't serve (which 404s on first chat). Prefer the stored
        // preference, then the backend's reported current, then the first model.
        const ids = response.models.map(m => m.id);
        const storedModel = localStorage.getItem('preferredAiModel');
        if (storedModel && ids.includes(storedModel)) {
          setCurrentModel(storedModel);
        } else if (response.current && ids.includes(response.current)) {
          setCurrentModel(response.current);
        } else if (response.models.length > 0) {
          setCurrentModel(response.models[0].id);
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching AI models:', err);
      setError(err.message || 'Failed to fetch AI models');
    } finally {
      setLoading(false);
    }
  }, []);

  // Change the current model
  const changeModel = useCallback(async (modelId) => {
    setLoading(true);
    setError(null);
    
    try {
      await chatApi.setCurrentModel(modelId);
      setCurrentModel(modelId);
      return true;
    } catch (err) {
      console.error('Error changing AI model:', err);
      setError(err.message || 'Failed to change AI model');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize on mount, and re-fetch when the user switches provider (#106) so
  // the dropdown reflects the newly-selected source's catalogue.
  useEffect(() => {
    fetchModels();
    const onProviderChange = () => fetchModels({ force: true });  // #492: catalogue changed
    window.addEventListener(AI_PROVIDER_CHANGED_EVENT, onProviderChange);
    return () => window.removeEventListener(AI_PROVIDER_CHANGED_EVENT, onProviderChange);
  }, [fetchModels]);

  return {
    models,
    currentModel,
    loading,
    error,
    metadata,
    fetchModels,
    changeModel,
    isUsingFallbackModels: metadata?.source?.startsWith('hardcoded-'),
    isApiSuccessful: metadata?.source === 'azure-api',
    lastUpdated: metadata?.last_updated ? new Date(metadata.last_updated) : null
  };
};

export default useAiModels;